import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import { Grid3X3, Plus, Save, X, Archive, Minus, Settings2, Wand2 } from 'lucide-react';
import { supabase } from '../../supabase';
import { useAuth } from '../../context/AuthContext';
import { useSite } from '../../context/SiteContext';
import { fetchMaestrosTalleColor } from '../../utils/talleColorMaestros';
import {
  isVariantTracked,
  buildVariantGrid,
  buildVariantCombinations,
  buildVariantSku,
  parseTalleList,
  applyStockDelta,
  getVariantAttributesLabel,
  getVariantStockForSite,
  buildVariantSalesMap,
} from '../../utils/variantStock';

const VARIANT_SELECT = 'id, product_id, talle, talle_orden, color_id, sku, barcode, price, precio_override, costo_override, activo, archivado_at, attributes, colores(id, nombre, hex, codigo_corto)';

const Swatch = ({ hex, size = 16 }) => (
  <span
    className="inline-block rounded-full border border-slate-300 shrink-0"
    style={{ width: size, height: size, background: hex || 'repeating-linear-gradient(45deg,#e2e8f0,#e2e8f0 3px,#fff 3px,#fff 6px)' }}
  />
);

const defaultCodigo = (product) =>
  buildVariantSku({ codigo: product?.sku || String(product?.name || '').split(/\s+/).slice(0, 2).join(' ') });

/**
 * Talles y colores de un producto: activación (con reparto del stock existente)
 * y grilla talle × color con stock por sucursal. Toda escritura pasa por
 * fn_guardar_variantes_producto / fn_mover_stock.
 */
export default function VariantesMatrizEditor({ product, onChanged }) {
  const navigate = useNavigate();
  const { organization } = useAuth();
  const { sites = [] } = useSite() || {};

  const tracked = isVariantTracked(product);
  const orgEnabled = Boolean(organization?.enable_variant_stock);

  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [curvas, setCurvas] = useState([]);
  const [colores, setColores] = useState([]);
  const [variants, setVariants] = useState([]);
  const [stockVariantes, setStockVariantes] = useState([]);
  const [stockProducto, setStockProducto] = useState([]);
  const [siteId, setSiteId] = useState('');
  // Ventas por talle (últimos 30 días): referencia para reponer la curva.
  const [ventasRows, setVentasRows] = useState([]);

  // --- activación ---
  const [curvaId, setCurvaId] = useState('');
  const [talles, setTalles] = useState([]);
  const [talleExtra, setTalleExtra] = useState('');
  const [colorIds, setColorIds] = useState([]);
  const [codigoBase, setCodigoBase] = useState(() => defaultCodigo(product));
  const [distribucion, setDistribucion] = useState({}); // key `${siteId}|${talle}|${colorId}` -> qty

  // --- edición ---
  const [celda, setCelda] = useState(null); // { variant }
  const [celdaForm, setCeldaForm] = useState({});
  const [ajuste, setAjuste] = useState({ cantidad: '', motivo: '' });
  const [agregarTalles, setAgregarTalles] = useState('');
  const [agregarColores, setAgregarColores] = useState([]);

  const load = useCallback(async () => {
    if (!product?.id || !product?.organization_id) return;
    setLoading(true);
    try {
      const [maestros, varRes, svRes, slRes] = await Promise.all([
        fetchMaestrosTalleColor(supabase, product.organization_id),
        supabase.from('product_variants').select(VARIANT_SELECT).eq('product_id', product.id).is('archivado_at', null),
        supabase.from('stock_variantes').select('variant_id, site_id, quantity').eq('product_id', product.id),
        supabase.from('stock_levels').select('site_id, quantity').eq('product_id', product.id),
      ]);
      if (varRes.error) throw varRes.error;
      if (svRes.error) throw svRes.error;
      setCurvas(maestros.curvas);
      setColores(maestros.colores.filter((c) => c.activo !== false));
      setVariants(varRes.data || []);
      setStockVariantes(svRes.data || []);
      setStockProducto(slRes.data || []);

      // No bloquea la grilla: si falla, simplemente no se muestran ventas.
      const desde = new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).toISOString();
      const { data: ventasData, error: ventasError } = await supabase
        .from('sale_items')
        .select('variant_id, quantity, sales!inner(status, invoice_status, active, site_id, created_at)')
        .eq('product_id', product.id)
        .not('variant_id', 'is', null)
        .gte('sales.created_at', desde);
      if (ventasError) console.error('Grilla de talles: no se pudieron leer las ventas:', ventasError);
      setVentasRows(ventasData || []);
    } catch (e) {
      console.error(e);
      toast.error('No se pudieron cargar los talles y colores del producto.');
    } finally {
      setLoading(false);
    }
  }, [product?.id, product?.organization_id]);

  useEffect(() => { if (tracked || orgEnabled) load(); }, [load, tracked, orgEnabled]);

  const coloresById = useMemo(() => new Map(colores.map((c) => [c.id, c])), [colores]);
  const siteName = (id) => sites.find((s) => String(s.id) === String(id))?.nombre || 'Sucursal';

  // Sucursales con stock previo (para el reparto) + todas las sucursales activas.
  const stockPrevioPorSitio = useMemo(() => {
    const map = new Map();
    for (const row of stockProducto) map.set(String(row.site_id), (map.get(String(row.site_id)) || 0) + Number(row.quantity || 0));
    return map;
  }, [stockProducto]);

  const sitiosReparto = useMemo(() => {
    const ids = new Set(sites.map((s) => String(s.id)));
    for (const id of stockPrevioPorSitio.keys()) ids.add(id);
    return [...ids];
  }, [sites, stockPrevioPorSitio]);

  // ---------------------------------------------------------------------------
  if (!tracked && !orgEnabled) return null;

  // ======================= ACTIVACIÓN =======================
  const coloresSeleccionados = colorIds.map((id) => coloresById.get(id)).filter(Boolean);
  const combinaciones = buildVariantCombinations({ talles, colores: coloresSeleccionados });

  const elegirCurva = (id) => {
    setCurvaId(id);
    const curva = curvas.find((c) => c.id === id);
    setTalles(curva ? [...(curva.valores || [])] : []);
  };

  const toggleTalle = (t) => setTalles((prev) => (prev.includes(t) ? prev.filter((x) => x !== t) : [...prev, t]));
  const toggleColor = (id) => setColorIds((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));
  const sumarTallesExtra = () => {
    const nuevos = parseTalleList(talleExtra).filter((t) => !talles.some((x) => x.toLowerCase() === t.toLowerCase()));
    if (nuevos.length) setTalles((prev) => [...prev, ...nuevos]);
    setTalleExtra('');
  };

  const distKey = (sid, combo) => `${sid}|${combo.talle ?? ''}|${combo.color?.id ?? ''}`;
  const asignadoEnSitio = (sid) => combinaciones.reduce((acc, combo) => acc + (Number(distribucion[distKey(sid, combo)]) || 0), 0);

  const activar = async () => {
    if (!combinaciones.length) return toast.error('Elegí al menos un talle o un color.');
    for (const sid of sitiosReparto) {
      const previo = stockPrevioPorSitio.get(sid) || 0;
      const asignado = asignadoEnSitio(sid);
      if (previo > 0 && asignado > previo) {
        return toast.error(`En ${siteName(sid)} estás asignando ${asignado} y hay ${previo}.`);
      }
      if (previo < 0 && asignado !== 0) {
        return toast.error(`En ${siteName(sid)} el stock es negativo: queda en "Sin clasificar".`);
      }
    }
    const variantesPayload = combinaciones.map((combo) => ({
      talle: combo.talle,
      talle_orden: combo.talle_orden,
      color_id: combo.color?.id ?? null,
      sku: buildVariantSku({ codigo: codigoBase, colorCodigo: combo.color?.codigo_corto, talle: combo.talle }) || null,
    }));
    const distribucionPayload = [];
    for (const sid of sitiosReparto) {
      for (const combo of combinaciones) {
        const qty = Number(distribucion[distKey(sid, combo)]) || 0;
        if (qty > 0) distribucionPayload.push({ site_id: sid, talle: combo.talle, color_id: combo.color?.id ?? null, quantity: qty });
      }
    }
    const tieneStockSinRepartir = sitiosReparto.some((sid) => (stockPrevioPorSitio.get(sid) || 0) - asignadoEnSitio(sid) > 0);
    if (tieneStockSinRepartir && !window.confirm('Hay stock sin repartir: va a quedar en la variante "Sin clasificar" hasta que lo ajustes. ¿Continuar?')) return;

    setBusy(true);
    try {
      const { error } = await supabase.rpc('fn_guardar_variantes_producto', {
        p_product: product.id, p_variantes: variantesPayload, p_distribucion: distribucionPayload,
      });
      if (error) throw error;
      toast.success('Talles y colores activados');
      await onChanged?.();
    } catch (e) {
      console.error(e);
      toast.error(e?.message || 'No se pudieron activar los talles.');
    } finally {
      setBusy(false);
    }
  };

  if (!tracked) {
    return (
      <div className="mt-3 bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden">
        <div className="bg-[#0B2A36] px-4 py-2.5 flex items-center gap-2">
          <Grid3X3 size={16} className="text-[#3FF2AA]" />
          <span className="text-[10px] font-black text-white uppercase tracking-widest">Talles y colores</span>
        </div>
        <div className="p-4 space-y-5">
          {loading ? <p className="text-sm text-slate-400">Cargando…</p> : (
            <>
              {!curvas.length && !colores.length && (
                <div className="text-[12px] text-slate-600 flex items-center justify-between gap-3 bg-slate-50 rounded-xl p-3">
                  <span>Todavía no cargaste curvas de talles ni colores.</span>
                  <button type="button" onClick={() => navigate('/sistema/config-talles-colores')}
                    className="inline-flex items-center gap-1 text-[10px] font-black uppercase text-[#4f46e5]"><Settings2 size={14} /> Configurar</button>
                </div>
              )}

              <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                <div>
                  <p className="text-[10px] font-bold text-slate-500 uppercase tracking-widest mb-2">Talles</p>
                  <select className="w-full border border-slate-200 rounded-xl px-3 py-2 text-sm mb-2" value={curvaId} onChange={(e) => elegirCurva(e.target.value)}>
                    <option value="">Elegí una curva…</option>
                    {curvas.filter((c) => c.activo !== false).map((c) => <option key={c.id} value={c.id}>{c.nombre}</option>)}
                  </select>
                  <div className="flex flex-wrap gap-1.5 mb-2">
                    {talles.map((t) => (
                      <button key={t} type="button" onClick={() => toggleTalle(t)}
                        className="px-2.5 py-1 rounded-lg bg-[#4f46e5] text-white text-[11px] font-bold inline-flex items-center gap-1">
                        {t} <X size={11} />
                      </button>
                    ))}
                  </div>
                  <div className="flex gap-2">
                    <input className="flex-1 border border-slate-200 rounded-xl px-3 py-1.5 text-sm" placeholder="Agregar talles: 3XL, 4XL"
                      value={talleExtra} onChange={(e) => setTalleExtra(e.target.value)} onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); sumarTallesExtra(); } }} />
                    <button type="button" onClick={sumarTallesExtra} className="px-3 rounded-xl border border-slate-200 text-slate-500"><Plus size={14} /></button>
                  </div>
                </div>
                <div>
                  <p className="text-[10px] font-bold text-slate-500 uppercase tracking-widest mb-2">Colores <span className="normal-case font-medium text-slate-400">(opcional)</span></p>
                  <div className="flex flex-wrap gap-1.5">
                    {colores.map((c) => {
                      const on = colorIds.includes(c.id);
                      return (
                        <button key={c.id} type="button" onClick={() => toggleColor(c.id)}
                          className={`px-2.5 py-1 rounded-lg text-[11px] font-bold inline-flex items-center gap-1.5 border ${on ? 'border-[#4f46e5] bg-indigo-50 text-[#4f46e5]' : 'border-slate-200 text-slate-500'}`}>
                          <Swatch hex={c.hex} size={12} /> {c.nombre}
                        </button>
                      );
                    })}
                  </div>
                  <label className="block mt-4">
                    <span className="text-[10px] font-bold text-slate-500 uppercase tracking-widest">Código base para el SKU</span>
                    <input className="mt-1 w-full border border-slate-200 rounded-xl px-3 py-1.5 text-sm uppercase" value={codigoBase} onChange={(e) => setCodigoBase(e.target.value)} />
                    <span className="text-[10px] text-slate-400">Ej.: {buildVariantSku({ codigo: codigoBase, colorCodigo: coloresSeleccionados[0]?.codigo_corto, talle: talles[0] }) || '—'}</span>
                  </label>
                </div>
              </div>

              {combinaciones.length > 0 && (
                <div>
                  <p className="text-[10px] font-bold text-slate-500 uppercase tracking-widest mb-2">
                    {combinaciones.length} combinaciones · stock {stockPrevioPorSitio.size ? 'a repartir' : 'inicial'} por sucursal
                  </p>
                  <div className="overflow-x-auto border border-slate-100 rounded-xl">
                    <table className="min-w-full text-[12px]">
                      <thead className="bg-slate-50 text-slate-500">
                        <tr>
                          <th className="text-left px-3 py-2 font-bold">Variante</th>
                          {sitiosReparto.map((sid) => (
                            <th key={sid} className="px-3 py-2 font-bold text-right whitespace-nowrap">
                              {siteName(sid)}
                              {stockPrevioPorSitio.has(sid) && (
                                <span className={`block text-[10px] font-medium ${asignadoEnSitio(sid) > (stockPrevioPorSitio.get(sid) || 0) ? 'text-red-500' : 'text-slate-400'}`}>
                                  {asignadoEnSitio(sid)} de {stockPrevioPorSitio.get(sid)}
                                </span>
                              )}
                            </th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {combinaciones.map((combo) => (
                          <tr key={`${combo.talle}|${combo.color?.id}`} className="border-t border-slate-100">
                            <td className="px-3 py-1.5 whitespace-nowrap">
                              <span className="inline-flex items-center gap-2">
                                {combo.color && <Swatch hex={combo.color.hex} size={12} />}
                                {[combo.color?.nombre, combo.talle].filter(Boolean).join(' / ')}
                              </span>
                            </td>
                            {sitiosReparto.map((sid) => (
                              <td key={sid} className="px-2 py-1 text-right">
                                <input type="number" min="0" inputMode="numeric"
                                  className="w-20 border border-slate-200 rounded-lg px-2 py-1 text-right"
                                  value={distribucion[distKey(sid, combo)] ?? ''}
                                  onChange={(e) => setDistribucion((d) => ({ ...d, [distKey(sid, combo)]: e.target.value }))} />
                              </td>
                            ))}
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                  {[...stockPrevioPorSitio.values()].some((v) => v !== 0) && (
                    <p className="text-[11px] text-slate-500 mt-2">Lo que no repartas queda en la variante <b>“Sin clasificar”</b>: el total del producto no cambia.</p>
                  )}
                </div>
              )}

              <div className="flex justify-end">
                <button type="button" disabled={busy || !combinaciones.length} onClick={activar}
                  className="inline-flex items-center gap-2 px-5 py-2.5 rounded-xl bg-[#4f46e5] text-white text-[10px] font-black uppercase tracking-widest disabled:opacity-40">
                  <Save size={14} /> Activar talles y colores
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    );
  }

  // ======================= GRILLA =======================
  const grid = buildVariantGrid(variants, { stockVariantes, siteId: siteId || null });
  const totalFila = (colorId) => grid.talles.reduce((acc, t) => acc + (grid.getCell(colorId, t)?.stock || 0), 0);
  const totalColumna = (t) => grid.colores.reduce((acc, c) => acc + (grid.getCell(c.id, t)?.stock || 0), 0);
  const totalGeneral = grid.colores.reduce((acc, c) => acc + totalFila(c.id), 0);
  const faltanSku = variants.some((v) => !v.sku);
  const ventasPorVariante = buildVariantSalesMap(ventasRows, { siteId: siteId || null });
  const hayVentas = Object.keys(ventasPorVariante).length > 0;

  const abrirCelda = (variant) => {
    setCelda({ variant });
    setCeldaForm({
      sku: variant.sku || '',
      barcode: variant.barcode || '',
      precio_override: variant.precio_override ?? '',
      activo: variant.activo !== false,
    });
    setAjuste({ cantidad: '', motivo: '' });
  };

  const guardarVariantes = async (payload, okMsg) => {
    setBusy(true);
    try {
      const { error } = await supabase.rpc('fn_guardar_variantes_producto', { p_product: product.id, p_variantes: payload, p_distribucion: [] });
      if (error) throw error;
      toast.success(okMsg);
      await load();
      await onChanged?.();
      return true;
    } catch (e) {
      console.error(e);
      toast.error(e?.message || 'No se pudo guardar.');
      return false;
    } finally {
      setBusy(false);
    }
  };

  const guardarCelda = async () => {
    const v = celda.variant;
    const precio = celdaForm.precio_override === '' ? null : Number(celdaForm.precio_override);
    if (precio !== null && (!Number.isFinite(precio) || precio < 0)) return toast.error('Precio inválido.');
    const ok = await guardarVariantes([{
      id: v.id, talle: v.talle, color_id: v.color_id,
      sku: celdaForm.sku, barcode: celdaForm.barcode, precio_override: precio, activo: celdaForm.activo,
    }], 'Variante actualizada');
    if (ok) setCelda(null);
  };

  const archivarCelda = async () => {
    if (!window.confirm(`¿Archivar ${getVariantAttributesLabel(celda.variant)}? Deja de verse en el POS y la tienda; el historial se conserva.`)) return;
    const ok = await guardarVariantes([{ id: celda.variant.id, archivar: true }], 'Variante archivada');
    if (ok) setCelda(null);
  };

  const ajustarStock = async (signo) => {
    const cantidad = Number(ajuste.cantidad);
    if (!Number.isFinite(cantidad) || cantidad <= 0) return toast.error('Indicá una cantidad mayor a 0.');
    if (!siteId) return toast.error('Elegí una sucursal arriba para ajustar el stock.');
    setBusy(true);
    try {
      await applyStockDelta({
        supabase,
        organizationId: product.organization_id,
        productId: product.id,
        variantId: celda.variant.id,
        siteId,
        delta: signo * cantidad,
        tipo: null,
        motivo: ajuste.motivo?.trim() || `Ajuste desde grilla de talles (${getVariantAttributesLabel(celda.variant)})`,
        allowNegative: false,
      });
      toast.success('Stock ajustado');
      setAjuste({ cantidad: '', motivo: '' });
      await load();
      await onChanged?.();
    } catch (e) {
      toast.error(e.message);
    } finally {
      setBusy(false);
    }
  };

  const agregar = async () => {
    const nuevosTalles = parseTalleList(agregarTalles);
    const tallesFinal = nuevosTalles.length ? nuevosTalles : grid.talles.filter(Boolean);
    const coloresExistentes = grid.colores.filter((c) => c.id).map((c) => coloresById.get(c.id) || c);
    const coloresNuevos = agregarColores.map((id) => coloresById.get(id)).filter(Boolean);
    const coloresFinal = coloresNuevos.length ? [...coloresExistentes, ...coloresNuevos] : coloresExistentes;
    const tallesTodos = nuevosTalles.length ? [...grid.talles.filter(Boolean), ...nuevosTalles] : tallesFinal;
    const baseOrden = variants.reduce((m, v) => Math.max(m, v.talle_orden ?? -1), -1) + 1;
    const combos = buildVariantCombinations({ talles: tallesTodos, colores: coloresFinal })
      .filter((combo) => !grid.getCell(combo.color?.id ?? null, combo.talle ?? ''));
    if (!combos.length) return toast('No hay combinaciones nuevas para agregar.');
    const payload = combos.map((combo) => {
      const existente = variants.find((v) => String(v.talle || '').toLowerCase() === String(combo.talle || '').toLowerCase());
      return {
        talle: combo.talle,
        talle_orden: existente?.talle_orden ?? (baseOrden + nuevosTalles.indexOf(combo.talle)),
        color_id: combo.color?.id ?? null,
        sku: buildVariantSku({ codigo: defaultCodigo(product), colorCodigo: combo.color?.codigo_corto, talle: combo.talle }) || null,
      };
    });
    const ok = await guardarVariantes(payload, `Se agregaron ${payload.length} combinaciones`);
    if (ok) { setAgregarTalles(''); setAgregarColores([]); }
  };

  const generarSkus = async () => {
    const payload = variants.filter((v) => !v.sku).map((v) => ({
      id: v.id, talle: v.talle, color_id: v.color_id,
      sku: buildVariantSku({ codigo: defaultCodigo(product), colorCodigo: v.colores?.codigo_corto, talle: v.talle }),
    }));
    if (payload.length) await guardarVariantes(payload, `SKU generados: ${payload.length}`);
  };

  return (
    <div className="mt-3 bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden">
      <div className="bg-[#0B2A36] px-4 py-2.5 flex items-center justify-between gap-2">
        <div className="flex items-center gap-2">
          <Grid3X3 size={16} className="text-[#3FF2AA]" />
          <span className="text-[10px] font-black text-white uppercase tracking-widest">Talles y colores · stock</span>
        </div>
        <select className="bg-white/10 text-white text-[11px] rounded-lg px-2 py-1 border border-white/10" value={siteId} onChange={(e) => setSiteId(e.target.value)}>
          <option value="" className="text-slate-800">Todas las sucursales</option>
          {sites.map((s) => <option key={s.id} value={s.id} className="text-slate-800">{s.nombre}</option>)}
        </select>
      </div>

      <div className="p-4 space-y-4">
        {loading ? <p className="text-sm text-slate-400">Cargando…</p> : (
          <>
            <div className="overflow-x-auto">
              <table className="min-w-full text-[12px] border-separate border-spacing-1">
                <thead>
                  <tr>
                    <th className="text-left text-[10px] font-bold text-slate-400 uppercase px-2">Color \ Talle</th>
                    {grid.talles.map((t) => <th key={t} className="text-[11px] font-black text-slate-600 px-2">{t || '—'}</th>)}
                    <th className="text-[10px] font-bold text-slate-400 uppercase px-2">Total</th>
                  </tr>
                </thead>
                <tbody>
                  {grid.colores.map((c) => (
                    <tr key={c.id ?? 'sin-color'}>
                      <td className="px-2 py-1 whitespace-nowrap">
                        <span className="inline-flex items-center gap-2 font-semibold text-slate-700">
                          {c.id ? <Swatch hex={c.hex} /> : null}{c.nombre || 'Sin color'}
                        </span>
                      </td>
                      {grid.talles.map((t) => {
                        const cell = grid.getCell(c.id, t);
                        if (!cell) return <td key={t} className="text-center text-slate-200">·</td>;
                        const stock = cell.stock;
                        const tone = stock < 0 ? 'bg-red-50 text-red-600 border-red-200' : stock === 0 ? 'bg-slate-50 text-slate-400 border-slate-200' : 'bg-emerald-50 text-emerald-700 border-emerald-200';
                        return (
                          <td key={t} className="text-center">
                            <button type="button" onClick={() => abrirCelda(cell.variant)}
                              className={`w-full min-w-[3rem] px-2 py-1.5 rounded-lg border font-black ${tone} hover:ring-2 hover:ring-indigo-200`}
                              title={`${getVariantAttributesLabel(cell.variant)} · SKU ${cell.variant.sku || '—'}`}>
                              {stock}
                              {hayVentas && (
                                <span className="block text-[9px] font-bold text-slate-400 leading-tight">
                                  v. {ventasPorVariante[cell.variant.id] || 0}
                                </span>
                              )}
                            </button>
                          </td>
                        );
                      })}
                      <td className="text-center font-black text-slate-700">{totalFila(c.id)}</td>
                    </tr>
                  ))}
                  <tr>
                    <td className="px-2 text-[10px] font-bold text-slate-400 uppercase">Total</td>
                    {grid.talles.map((t) => <td key={t} className="text-center font-black text-slate-700">{totalColumna(t)}</td>)}
                    <td className="text-center font-black text-[#4f46e5]">{totalGeneral}</td>
                  </tr>
                </tbody>
              </table>
            </div>
            <p className="text-[10px] text-slate-400">
              Tocá una celda para editar SKU, código de barras y precio, o ajustar el stock de esa variante.
              {hayVentas && ' "v." = unidades vendidas en los últimos 30 días (sin anuladas ni presupuestos).'}
            </p>

            {celda && (
              <div className="border border-indigo-100 bg-indigo-50/40 rounded-2xl p-4 space-y-3">
                <div className="flex items-center justify-between">
                  <p className="text-sm font-black text-slate-800">{getVariantAttributesLabel(celda.variant)}</p>
                  <button type="button" onClick={() => setCelda(null)} className="text-slate-400"><X size={16} /></button>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
                  <label className="block"><span className="text-[10px] font-bold text-slate-500 uppercase">SKU</span>
                    <input className="mt-1 w-full border border-slate-200 rounded-xl px-3 py-1.5 text-sm" value={celdaForm.sku} onChange={(e) => setCeldaForm((f) => ({ ...f, sku: e.target.value }))} /></label>
                  <label className="block"><span className="text-[10px] font-bold text-slate-500 uppercase">Código de barras</span>
                    <input className="mt-1 w-full border border-slate-200 rounded-xl px-3 py-1.5 text-sm" value={celdaForm.barcode} onChange={(e) => setCeldaForm((f) => ({ ...f, barcode: e.target.value }))} /></label>
                  <label className="block"><span className="text-[10px] font-bold text-slate-500 uppercase">Precio propio</span>
                    <input type="number" min="0" className="mt-1 w-full border border-slate-200 rounded-xl px-3 py-1.5 text-sm" placeholder={`Usa el del producto (${product.sell_price ?? 0})`}
                      value={celdaForm.precio_override} onChange={(e) => setCeldaForm((f) => ({ ...f, precio_override: e.target.value }))} /></label>
                  <label className="flex items-center gap-2 mt-5 text-[12px] font-semibold text-slate-600">
                    <input type="checkbox" checked={celdaForm.activo} onChange={(e) => setCeldaForm((f) => ({ ...f, activo: e.target.checked }))} /> Activa (se vende)
                  </label>
                </div>
                <div className="flex flex-wrap justify-between gap-3">
                  <button type="button" disabled={busy} onClick={archivarCelda} className="inline-flex items-center gap-1.5 text-[10px] font-black uppercase text-slate-500 hover:text-red-500">
                    <Archive size={14} /> Archivar
                  </button>
                  <button type="button" disabled={busy} onClick={guardarCelda} className="inline-flex items-center gap-2 px-4 py-2 rounded-xl bg-[#4f46e5] text-white text-[10px] font-black uppercase tracking-widest">
                    <Save size={14} /> Guardar
                  </button>
                </div>
                <div className="border-t border-indigo-100 pt-3">
                  <p className="text-[10px] font-bold text-slate-500 uppercase mb-2">
                    Ajustar stock {siteId ? `en ${siteName(siteId)} (hoy: ${getVariantStockForSite(stockVariantes, celda.variant.id, siteId)})` : '— elegí una sucursal arriba'}
                  </p>
                  <div className="flex flex-wrap gap-2">
                    <input type="number" min="1" className="w-24 border border-slate-200 rounded-xl px-3 py-1.5 text-sm" placeholder="Cant."
                      value={ajuste.cantidad} onChange={(e) => setAjuste((a) => ({ ...a, cantidad: e.target.value }))} />
                    <input className="flex-1 min-w-[10rem] border border-slate-200 rounded-xl px-3 py-1.5 text-sm" placeholder="Motivo (opcional)"
                      value={ajuste.motivo} onChange={(e) => setAjuste((a) => ({ ...a, motivo: e.target.value }))} />
                    <button type="button" disabled={busy || !siteId} onClick={() => ajustarStock(1)} className="inline-flex items-center gap-1 px-3 py-1.5 rounded-xl bg-emerald-500 text-white text-[10px] font-black uppercase disabled:opacity-40"><Plus size={13} /> Sumar</button>
                    <button type="button" disabled={busy || !siteId} onClick={() => ajustarStock(-1)} className="inline-flex items-center gap-1 px-3 py-1.5 rounded-xl bg-red-500 text-white text-[10px] font-black uppercase disabled:opacity-40"><Minus size={13} /> Restar</button>
                  </div>
                </div>
              </div>
            )}

            <div className="border-t border-slate-100 pt-4 grid grid-cols-1 md:grid-cols-[1fr_1fr_auto] gap-3 items-end">
              <label className="block"><span className="text-[10px] font-bold text-slate-500 uppercase">Agregar talles</span>
                <input className="mt-1 w-full border border-slate-200 rounded-xl px-3 py-1.5 text-sm" placeholder="3XL, 4XL" value={agregarTalles} onChange={(e) => setAgregarTalles(e.target.value)} /></label>
              <div>
                <span className="text-[10px] font-bold text-slate-500 uppercase">Agregar colores</span>
                <div className="mt-1 flex flex-wrap gap-1.5">
                  {colores.filter((c) => !grid.colores.some((g) => g.id === c.id)).map((c) => {
                    const on = agregarColores.includes(c.id);
                    return (
                      <button key={c.id} type="button" onClick={() => setAgregarColores((p) => (on ? p.filter((x) => x !== c.id) : [...p, c.id]))}
                        className={`px-2 py-0.5 rounded-lg text-[11px] font-bold inline-flex items-center gap-1 border ${on ? 'border-[#4f46e5] bg-indigo-50 text-[#4f46e5]' : 'border-slate-200 text-slate-500'}`}>
                        <Swatch hex={c.hex} size={10} /> {c.nombre}
                      </button>
                    );
                  })}
                </div>
              </div>
              <div className="flex gap-2">
                {faltanSku && (
                  <button type="button" disabled={busy} onClick={generarSkus} className="inline-flex items-center gap-1.5 px-3 py-2 rounded-xl border border-slate-200 text-slate-600 text-[10px] font-black uppercase"><Wand2 size={13} /> Generar SKU</button>
                )}
                <button type="button" disabled={busy} onClick={agregar} className="inline-flex items-center gap-1.5 px-4 py-2 rounded-xl bg-[#4f46e5] text-white text-[10px] font-black uppercase"><Plus size={13} /> Agregar</button>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
